import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Skeleton,
  Stack,
  Badge,
  Avatar,
} from "@mui/material";
import {
  Search,
  Clear,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Person,
  FilterList,
  Refresh,
  Block,
  PersonAdd,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import ConfirmationDialog from "../../components/UI/ConfirmationDialog";
import { toast } from "react-toastify";

/**
 * User Management Page (Admin Only)
 * Features:
 * - List all users with pagination
 * - Search and filter users
 * - Edit user details
 * - Activate/deactivate users
 * - Delete users
 */
export default function UserManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    user_status: "",
    role: "",
  });

  const fetchData = useCallback(() => {
    setLoading(true);

    const option = {
      page: page,
      count: rowsPerPage,
      keyword: keyword,
      status_filter: statusFilter,
      role_filter: roleFilter,
    };

    myApi
      .post("/admin/users", option)
      .then((res) => {
        const result = res.data;
        setUsers(result.data || []);
        setTotalItems(result.total || 0);
        setTotalPages(result.totalPages || 1);
        setLoading(false);
      })
      .catch((err) => {
        showSnackbar(err.message || "Error fetching users", "error");
        setLoading(false);
      });
  }, [page, rowsPerPage, keyword, statusFilter, roleFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearch = (e) => {
    if (e.target.name === "keyword") {
      setKeyword(e.target.value);
      setPage(1);
    } else if (e.target.name === "filterBy") {
      // Reserved for future use
    }
  };

  const handleClearSearch = () => {
    setKeyword("");
    setStatusFilter("");
    setRoleFilter("");
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const getStatusChip = (status) => {
    const statusMap = {
      active: { label: "Active", color: "success" },
      inactive: { label: "Inactive", color: "error" },
    };
    const config = statusMap[status] || { label: status, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getRoleChip = (role) => {
    const roleMap = {
      admin: { label: "Admin", color: "error" },
      peserta: { label: "Participant", color: "primary" },
    };
    const config = roleMap[role] || { label: role, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      organization: user.organization || "",
      user_status: user.user_status || "",
      role: user.role || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const response = await myApi.put(`/admin/users/${selectedUser._id}`, editForm);
      
      if (response.data && response.data.status === "success") {
        toast.success("User updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setEditDialogOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.message || "Failed to update user", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating user", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await myApi.del(`/admin/users/${selectedUser._id}`);
      
      if (response.data && response.data.status === "success") {
        toast.success("User deleted successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setDeleteDialogOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.message || "Failed to delete user", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting user", {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.user_status === "active" ? "inactive" : "active";
    const action = newStatus === "active" ? "activate" : "deactivate";
    
    setConfirmAction({
      userId: user._id,
      action: action,
      userName: user.name,
      newStatus: newStatus,
    });
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    try {
      const response = await myApi.put(`/admin/users/${confirmAction.userId}`, {
        user_status: confirmAction.newStatus,
      });

      if (response.data && response.data.status === "success") {
        toast.success(`User ${confirmAction.action}d successfully!`, {
          position: "top-right",
          autoClose: 3000,
        });
        setConfirmDialogOpen(false);
        fetchData();
      } else {
        toast.error(response.data?.message || `Failed to ${confirmAction.action} user`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error ${confirmAction.action}ing user`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Get current user ID to prevent self-deletion
  const currentUser = myApi.getUser();
  const currentUserId = currentUser?._id || currentUser?.id;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage all registered users and their accounts
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              name="keyword"
              placeholder="Search by name, email, or organization..."
              value={keyword}
              onChange={handleSearch}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "action.active" }} />,
                endAdornment: keyword && (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="peserta">Participant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Organization</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Registered</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(rowsPerPage)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton variant="text" width={150} /></TableCell>
                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                    <TableCell><Skeleton variant="text" width={150} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="info" sx={{ my: 2 }}>
                      No users found. Adjust your filters or search terms.
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar 
                          src={user.profile_picture || undefined}
                          sx={{ width: 36, height: 36, bgcolor: "grey.400" }}
                        >
                          {user.name?.charAt(0) || "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.phone || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.organization || "-"}</TableCell>
                    <TableCell>{getRoleChip(user.role)}</TableCell>
                    <TableCell>{getStatusChip(user.user_status)}</TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit User">
                          <IconButton size="small" color="primary" onClick={() => handleEditClick(user)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user._id === currentUserId ? (
                          <Tooltip title="Cannot modify your own account">
                            <span>
                              <IconButton
                                size="small"
                                color={user.user_status === "active" ? "warning" : "success"}
                                disabled
                              >
                                {user.user_status === "active" ? <Block fontSize="small" /> : <PersonAdd fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title={user.user_status === "active" ? "Deactivate" : "Activate"}>
                            <IconButton
                              size="small"
                              color={user.user_status === "active" ? "warning" : "success"}
                              onClick={() => handleToggleStatus(user)}
                            >
                              {user.user_status === "active" ? <Block fontSize="small" /> : <PersonAdd fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {user._id === currentUserId ? (
                          <Tooltip title="Cannot delete your own account">
                            <span>
                              <IconButton size="small" color="error" disabled>
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Delete User">
                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(user)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization"
                value={editForm.organization}
                onChange={(e) => setEditForm({ ...editForm, organization: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>User Status</InputLabel>
                <Select
                  value={editForm.user_status}
                  label="User Status"
                  onChange={(e) => setEditForm({ ...editForm, user_status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  label="Role"
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="peserta">Participant</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete "${selectedUser?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Status Toggle Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmAction}
        title={confirmAction?.action === "activate" ? "Activate User" : "Deactivate User"}
        message={
          confirmAction?.action === "activate"
            ? `Are you sure you want to activate "${confirmAction?.userName}"?`
            : `Are you sure you want to deactivate "${confirmAction?.userName}"?`
        }
        confirmText={confirmAction?.action === "activate" ? "Activate" : "Deactivate"}
        cancelText="Cancel"
        variant={confirmAction?.action === "activate" ? "success" : "warning"}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Container>
  );
}
